<script setup>
import { computed, ref, watch } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useProfileStore } from '@/stores/profile'
import { useUsersStore } from '@/stores/users'
import Modal from '@/components/UTAModal.vue'
import userName from '@/helpers/userName'
import checkArrayIncludes from '@/helpers/checkArrayIncludes'

// STORE
const translaterStore   = useTranslaterStore()
const profileStore      = useProfileStore()
const usersStore        = useUsersStore()

const getTranslate      = computed(() => translaterStore.getTranslate)
const profile           = computed(() => profileStore.profile)
const users             = computed(() => usersStore.users)
const usersProcess      = computed(() => usersStore.usersProcess)
const usersMsg          = computed(() => usersStore.usersMsg)
const userDeleteProcess = computed(() => usersStore.userDeleteProcess)
const userDeleteMsg     = computed(() => usersStore.userDeleteMsg)

const pageNumber        = ref(1)
const viewMode          = ref(localStorage.getItem('UTA_USERS_VIEW') || 'line')
const searchMode        = ref(null)
const searchModeText    = ref('USERS_SEARCH_MODE_ALL')
const inputSearch       = ref('')
const deleteUserUUID    = ref('')
const showUsersDelete   = ref(false)

function changePage(cursor, dir) {

    const params = {

        'c.limit': 12, 
        'c.direction': dir, 
        'c.cursor': cursor
    }

    fetchUsers(params).then(users => {

        if(users.value.array) {

            dir == 'after' ? 
                pageNumber.value++ : 
                pageNumber.value--
        }
    })
}

async function fetchUsers(outerParams) {

    let params = {

        'c.limit': 12
    }

    if(inputSearch.value.length > 2)
        params['c.q'] = inputSearch.value

    if(searchMode.value) 
        params['c.filterQ'] = searchMode.value

    if(outerParams) 
        params = { ...params, ...outerParams }

    usersStore.readUsers(params)

    return users
}

function setSearchMode(mode) {

    if(mode) {

        searchMode.value = mode

        switch(mode) {

            case 'email': 
                searchModeText.value = 'USERS_SEARCH_MODE_EMAIL'
                break

            case 'phone': 
                searchModeText.value = 'USERS_SEARCH_MODE_PHONE'
                break

            case 'name': 
                searchModeText.value = 'USERS_SEARCH_MODE_NAME'
                break

            case 'midname': 
                searchModeText.value = 'USERS_SEARCH_MODE_MIDNAME'
                break

            case 'surname': 
                searchModeText.value = 'USERS_SEARCH_MODE_SURNAME'
                break
        }

    } else {

        searchMode.value = null
        searchModeText.value = 'USERS_SEARCH_MODE_ALL'
    }

    fetchUsers()
}

function setViewMode(mode) {

    viewMode.value = mode
    localStorage.setItem('UTA_USERS_VIEW', mode)
}

viewMode.value = localStorage.getItem('UTA_USERS_VIEW') || 'grid'
fetchUsers()

watch(inputSearch, () => {

    pageNumber.value = 1
    fetchUsers()
})

watch(userDeleteMsg, msg => {

    if('success' in msg && msg.success) {

        showUsersDelete.value = false
        fetchUsers()
    }
})

</script>

<template>
    <div class="my-4 uta-users">
        <div class="container-fluid">
            <header class="uta-header" role="heading" aria-level="1">
                <h1 class="uta-header__title">{{ getTranslate.USERS }}</h1>
                <div class="spinner-border uta-header__spinner" role="status" v-if="usersProcess">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <div class="uta-header__actions">
                    <router-link 
                        v-if="
                            checkArrayIncludes(profile.attr?.array, 'SUPER_ADMIN') || 
                            checkArrayIncludes(profile.attr?.array, 'GROUP_ADMIN') && profile.ugroup
                        "
                        :to="'/users/create'" 
                        class="uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon uta-header__action uta-header__action--create">
                        <span class="uta-btn__content">
                            <span class="uta-btn__icon material-icons">
                                add
                            </span>
                            <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_ADD }}</span>
                        </span>
                    </router-link>
                </div>
            </header>
            <div class="row mb-4 justify-content-between">
                <div class="col-12 col-md-8 col-lg-10">
                    <div class="input-group uta-input__wrapper uta-search">
                        <button class="btn btn-outline-secondary dropdown-toggle uta-search__filter-btn" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                            {{ getTranslate[searchModeText] }}
                        </button>
                        <ul class="dropdown-menu uta-search__filter-menu">
                            <li class="uta-search__filter-menu-item">
                                <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('')">{{ getTranslate.USERS_SEARCH_MODE_ALL }}</a>
                            </li>
                            <li  class="uta-search__filter-menu-item">
                                <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('email')">{{ getTranslate.USERS_SEARCH_MODE_EMAIL }}</a>
                            </li>
                            <li  class="uta-search__filter-menu-item">
                                <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('phone')">{{ getTranslate.USERS_SEARCH_MODE_PHONE }}</a>
                            </li>
                            <li  class="uta-search__filter-menu-item">
                                <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('name')">{{ getTranslate.USERS_SEARCH_MODE_NAME }}</a>
                            </li>
                            <li  class="uta-search__filter-menu-item">
                                <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('midname')">{{ getTranslate.USERS_SEARCH_MODE_MIDNAME }}</a>
                            </li>
                            <li  class="uta-search__filter-menu-item">
                                <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('surname')">{{ getTranslate.USERS_SEARCH_MODE_SURNAME }}</a>
                            </li>
                        </ul>
                        <input 
                            type="text" 
                            class="form-control uta-input__wrapper-input uta-search__input"
                            :placeholder="getTranslate.SEARCH + '...'" 
                            v-model="inputSearch"
                            @keyup.escape="inputSearch = '', fetchUsers()"
                        >
                        <template v-if="inputSearch.length > 2">
                            <span class="material-icons uta-input__wrapper-icon" @click="inputSearch = '', fetchUsers()">
                                cancel
                            </span>
                        </template>
                        <template v-else>
                            <span class="material-icons uta-input__wrapper-icon">
                                search
                            </span>
                        </template>
                    </div>
                </div>
                <div class="d-none d-md-flex view-selector">
                    <a href="#" class="view-selector__item view-selector__item--lines" @click.prevent="setViewMode('lines')"></a>
                    <a href="#" class="view-selector__item view-selector__item--grid" @click.prevent="setViewMode('grid')"></a>
                </div>
            </div>
            <div 
                v-if="'success' in usersMsg && !usersMsg.success"
                class="uta-alert uta-alert--danger my-4"
            >
                <div class="uta-alert__text">
                    {{ getTranslate[usersMsg.text] || usersMsg.text }}
                </div>
            </div>
            <div 
                v-if="'success' in userDeleteMsg && userDeleteMsg.success"
                class="uta-alert uta-alert--success my-4"
            >
                <div class="uta-alert__text">
                    {{ getTranslate[userDeleteMsg.text] }}
                </div>
                <a href="#" class="uta-alert__close" @click.prevent="usersStore.setDeleteUserMsg({})">
                    <span class="material-icons">
                        close
                    </span>
                </a>
            </div>
            <template v-if="users.array">
                <template v-if="viewMode == 'lines'">
                    <div class="container-fluid">
                        <div class="uta-users__list uta-list">
                            <div class="uta-list__title d-none d-lg-block uta-users__title">
                                <div class="row">
                                    <div class="col-10">
                                        <div class="row">
                                            <div class="col-12 col-lg-4">{{ getTranslate.USER_NAME }}</div>
                                            <div class="col-12 col-lg-4">{{ getTranslate.USER_EMAIL }}</div>
                                            <div class="col-12 col-lg-4">{{ getTranslate.USER_PHONE }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="uta-list__content">
                                <div 
                                    class="row align-items-center uta-list__item uta-users__user uta-users__user--lines" 
                                    :class="{ 'uta-list__item--dark' : index % 2 }" 
                                    v-for="(user, index) in users.array" 
                                    :key="user.uuid"
                                >
                                    <div class="col-10">
                                        <div class="row">
                                            <div class="col-12 col-lg-4 uta-users__user-name">
                                                <span class="d-inline d-lg-none uta-users__user-mobile-title">{{ getTranslate.USER_NAME }}: </span>
                                                <router-link :to="'/users/' + user.uuid" class="uta-list__item-link uta-users__link">{{ userName(user) || getTranslate.USERS_NEW_USER }}</router-link>
                                            </div>
                                            <div class="col-12 col-lg-4 uta-users__user-email">
                                                <span class="d-inline d-lg-none uta-users__user-mobile-title">{{ getTranslate.USER_EMAIL }}: </span>
                                                {{ user.email || "$mdash;" }}
                                            </div>
                                            <div class="col-12 col-lg-4 uta-users__user-name">
                                                <span class="d-inline d-lg-none uta-users__user-mobile-title">{{ getTranslate.USER_PHONE }}: </span>
                                                {{ user.phone || "$mdash;" }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-2 text-end">
                                        <a 
                                            href="#"
                                            class="uta-users__user-delete"
                                            @click="
                                                usersStore.setDeleteUserMsg({}), 
                                                deleteUserUUID = user.uuid, 
                                                showUsersDelete = true
                                            "
                                        >
                                            <span class="material-icons">
                                                delete
                                            </span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <template v-if="viewMode == 'grid'">
                    <div class="row">
                        <div 
                            class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3 col-xxl-2 mb-5" 
                            v-for="(user, index) in users.array" 
                            :key="index"
                        >
                            <div class="uta-users__user uta-users__user--grid">
                                <a 
                                    hrf="#"
                                    class="uta-users__user-delete" 
                                    @click.prevent="
                                        usersStore.setDeleteUserMsg({}), 
                                        deleteUserUUID = user.uuid, 
                                        showUsersDelete = true
                                    "
                                >
                                    <span class="material-icons">
                                        delete
                                    </span>
                                </a>
                                <div class="uta-users__user-name">{{ userName(user) || getTranslate.USERS_NEW_USER }}</div>
                                <div class="row">
                                    <div class="col-12">
                                        <div class="uta-users__user-email">
                                            {{ getTranslate.USER_EMAIL }}: {{ user.email }}
                                        </div>
                                    </div>
                                    <div class="col-12">
                                        <div class="uta-users__user-number">
                                            {{ getTranslate.USER_PHONE }}: {{ user.phone }}
                                        </div>
                                    </div>
                                </div>
                                <div class="row uta-users__user-readmore">
                                    <div class="col-12">
                                        <router-link :to="'/users/' + user.uuid" class="uta-users__user-readmore-link">
                                            {{ getTranslate.USERS_READMORE }} 
                                            <span class="material-icons">
                                                navigate_next
                                            </span>
                                        </router-link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </template>
            <template v-else>
                <div class="uta-alert uta-alert--danger">
                    {{ getTranslate.USERS_EMPTY }}
                </div>
            </template>
            <div class="my-4 uta-users__pagination" v-if="users.c && (users.c.isBefore || users.c.isAfter)">
                <div class="row">
                    <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3 col-xxl-2">
                        <div class="uta-pagination">
                            <button 
                                :disabled="usersProcess || !users.c.isBefore"
                                class="uta-btn uta-btn--sm uta-pagination__prev" 
                                @click.prevent="changePage(users.c.start, 'before')"
                            >
                                <span class="material-icons">
                                    navigate_before
                                </span>
                            </button>
                            <div class="uta-pagination__current">
                                {{ pageNumber }}
                            </div>
                            <button 
                                :disabled="usersProcess || !users.c.isAfter"
                                class="uta-btn uta-btn--sm uta-pagination__next" 
                                @click.prevent="changePage(users.c.stop, 'after')"
                            >
                                <span class="material-icons">
                                    navigate_next
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <transition name="bounce">
            <Modal 
                v-if="showUsersDelete"
                :header="getTranslate.USERS_DELETE"
                size="md"
                @closeModal="showUsersDelete = false"
            >
                <template v-slot:body>
                    <div
                        v-if="'success' in userDeleteMsg && !userDeleteMsg.success" 
                        class="mb-4 uta-alert uts-alert--danger"
                    >
                        {{ getTranslate[userDeleteMsg.text] || userDeleteMsg.text }}
                    </div>
                    {{ getTranslate.USERS_DELETE_DESCR }}
                    <div class="mt-4 uta-btns">
                        <a 
                            href="#" 
                            class="uta-btn uta-btn--main"
                            @click.prevent="usersStore.deleteUser(deleteUserUUID)"
                        >
                            <template v-if="!userDeleteProcess">
                                {{ getTranslate.BTN_DELETE }}
                            </template>
                            <template v-else>
                                <div class="spinner-border" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                            </template>
                        </a>
                    </div>
                </template>
            </Modal>
        </transition>
    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/users';
</style>